import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { CryptoAlertTile } from "@/components/CryptoAlertTile";
import { TabSelector } from "@/components/TabSelector";
import {
  useInfiniteListings,
  useInfiniteDelistings,
} from "@/hooks/useCryptoAlerts";
import {
  CryptoAlertData,
  PositionData,
  ClosedPositionData,
  AlertTier,
} from "@crypto-trading/common/browser";
import { Loader2, Brain } from "lucide-react";
import { TierSelector } from "@/components/TierSelector";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { trackEvent } from "@/utils/analytics";
import { DesktopLayout } from "@/layouts";

// Desktop-specific drawer wrappers
import { AlertDetailDrawerDesktop } from "./drawers/AlertDetailDrawer.Desktop";
import { PositionDetailDrawerDesktop } from "./drawers/PositionDetailDrawer.Desktop";
import { PositionTradeDrawerDesktop } from "./drawers/PositionTradeDrawer.Desktop";
import { TickerSymbolDrawerDesktop } from "./drawers/TickerSymbolDrawer.Desktop";
import { TokenAnalysisDrawerDesktop } from "./drawers/TokenAnalysisDrawer.Desktop";

const ALERTS_PER_PAGE = 10; // More alerts per page on desktop

/**
 * Desktop version of the Alerts page
 * - No right sidebar
 * - 2-column grid for alerts (524px each)
 * - Analyze Token button in header
 * - Right-side sheets instead of bottom drawers
 */
export const AlertsPageDesktop: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [activeTab, setActiveTab] = useState<"listings" | "delistings">(
    "listings"
  );
  const [selectedPosition, setSelectedPosition] = useState<
    PositionData | ClosedPositionData | null
  >(null);
  const [selectedAlert, setSelectedAlert] = useState<CryptoAlertData | null>(
    null
  );
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<"ALL" | AlertTier>(
    AlertTier.TIER_1
  );
  const [isTickerDrawerOpen, setIsTickerDrawerOpen] = useState(false);
  const [isAnalysisDrawerOpen, setIsAnalysisDrawerOpen] = useState(false);
  const [analysisSymbol, setAnalysisSymbol] = useState<string | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tierFilterValue = tierFilter === "ALL" ? undefined : tierFilter;
  const tierOptions = [
    { value: "ALL" as const, label: "All" },
    { value: AlertTier.TIER_1 as const, label: "Tier 🔥🔥🔥" },
    { value: AlertTier.TIER_2 as const, label: "Tier 🔥🔥" },
    { value: AlertTier.TIER_3 as const, label: "Tier 🔥" },
  ];

  // React Query hooks
  const {
    data: listingsData,
    isLoading: isLoadingListings,
    fetchNextPage: fetchNextListings,
    hasNextPage: hasNextListings,
    isFetchingNextPage: isFetchingNextListings,
  } = useInfiniteListings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "listings",
    tierFilterValue
  );

  const {
    data: delistingsData,
    isLoading: isLoadingDelistings,
    fetchNextPage: fetchNextDelistings,
    hasNextPage: hasNextDelistings,
    isFetchingNextPage: isFetchingNextDelistings,
  } = useInfiniteDelistings(
    userId,
    ALERTS_PER_PAGE,
    activeTab === "delistings",
    tierFilterValue
  );

  // Flatten alerts from all pages
  const alerts =
    activeTab === "listings"
      ? (listingsData?.pages.flatMap((page) => page.alerts) ?? [])
      : (delistingsData?.pages.flatMap((page) => page.alerts) ?? []);

  const isLoading =
    activeTab === "listings" ? isLoadingListings : isLoadingDelistings;
  const hasMore =
    activeTab === "listings"
      ? (hasNextListings ?? false)
      : (hasNextDelistings ?? false);
  const isFetchingNextPage =
    activeTab === "listings"
      ? isFetchingNextListings
      : isFetchingNextDelistings;
  const fetchNextPage =
    activeTab === "listings" ? fetchNextListings : fetchNextDelistings;

  const handleViewPositionFromAlert = (positionId: string) => {
    const alert = alerts.find((a) => a.userPosition?.id === positionId);
    if (alert?.userPosition) {
      const positionData: PositionData = {
        positionId: alert.userPosition.id,
        symbol: alert.userPosition.symbol,
        positionType: alert.userPosition.positionType as "LONG" | "SHORT",
        amount: alert.userPosition.amount.toString(),
        leverage: alert.userPosition.leverage.toString(),
        entryPrice: alert.userPosition.entryPrice.toString(),
        markPrice: alert.userPosition.markPrice?.toString() || "0",
        exitPrice: alert.userPosition.exitPrice?.toString() || "0",
        pnl: alert.userPosition.realizedPnl?.toString() || "0",
        pnlPercentage: alert.userPosition.pnlPercentage?.toString() || "0",
        exchangeCode: alert.exchange,
        exchangeName: alert.exchange,
        usdAmount: alert.userPosition.usdAmount.toString(),
        openedAt: alert.userPosition.openedAt?.toString() || undefined,
        closedAt: alert.userPosition.closedAt?.toString() || undefined,
      };
      setSelectedPosition(positionData);
    }
  };

  const handleOpenTradeDrawer = (alert: CryptoAlertData) => {
    setTradeSymbol(alert.currency ? ensureUsdtSymbol(alert.currency) : null);
    setIsTradeDrawerOpen(true);
  };

  const handleOpenAnalysisDrawer = () => {
    trackEvent("custom_trade_attempt", { page: "alerts", userId });
    setIsTickerDrawerOpen(true);
  };

  const handleTickerSubmit = useCallback((normalizedSymbol: string) => {
    setAnalysisSymbol(normalizedSymbol);
    setIsTickerDrawerOpen(false);
    setIsAnalysisDrawerOpen(true);
  }, []);

  const handleTradeFromAnalysis = useCallback((symbol: string) => {
    setTradeSymbol(symbol);
    setIsAnalysisDrawerOpen(false);
    setIsTradeDrawerOpen(true);
  }, []);

  useEffect(() => {
    if (selectedAlert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: selectedAlert.id,
        symbol: selectedAlert.currency,
        alertType: selectedAlert.type,
        tier: selectedAlert.tier ?? null,
        exchange: selectedAlert.exchange,
      });
    }
  }, [selectedAlert?.id]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, isLoading, isFetchingNextPage, activeTab, fetchNextPage]);

  // Header action button for Analyze Token
  const headerActions = (
    <button
      onClick={handleOpenAnalysisDrawer}
      className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white font-medium rounded-lg transition-all duration-150 shadow-lg hover:shadow-accent/20"
    >
      <Brain className="w-4 h-4" />
      <span>Analyze Token</span>
    </button>
  );

  return (
    <>
      <DesktopLayout
        pageTitle="Alerts"
        pageDescription="Real-time cryptocurrency listing and delisting alerts"
        showRightSidebar={false}
        headerActions={headerActions}
      >
        {/* Tab Toggle */}
        <div className="w-64 mb-3">
          <TabSelector
            options={[
              { value: "listings", label: "Listings" },
              { value: "delistings", label: "Delistings" },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>
        {/* Filters Row */}
        <div className="flex items-center gap-6 mb-6">
          {/* Tier Filter */}
          <div className="flex-1">
            <TierSelector<typeof tierFilter>
              options={tierOptions}
              value={tierFilter}
              onChange={(value) => setTierFilter(value)}
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && alerts.length === 0 && (
          <div className="text-center py-12 bg-tertiary border border-border rounded-xl">
            <p className="text-text-secondary">No {activeTab} found</p>
          </div>
        )}

        {/* Alerts Grid - 2 columns with 524px width each */}
        {!isLoading && alerts.length > 0 && (
          <div
            className="grid gap-4 justify-center"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(480px, 524px))",
            }}
          >
            {alerts.map((alert) => (
              <div key={alert.id} className="w-full max-w-[524px]">
                <CryptoAlertTile
                  alert={alert}
                  onViewPosition={handleViewPositionFromAlert}
                  onDetailsClick={() => setSelectedAlert(alert)}
                  onTrade={handleOpenTradeDrawer}
                />
              </div>
            ))}
          </div>
        )}

        {/* Load More Trigger */}
        {!isLoading && hasMore && (
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {isFetchingNextPage && (
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            )}
          </div>
        )}

        {/* End of List */}
        {!isLoading && !hasMore && alerts.length > 0 && (
          <div className="text-center py-8">
            <p className="text-text-secondary text-sm">
              No more {activeTab} to load
            </p>
          </div>
        )}
      </DesktopLayout>

      {/* Desktop Sheet Drawers */}
      <AlertDetailDrawerDesktop
        userId={userId}
        alert={selectedAlert}
        open={!!selectedAlert}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        onTrade={(alert) => {
          setTradeSymbol(
            alert.currency ? ensureUsdtSymbol(alert.currency) : null
          );
          setIsTradeDrawerOpen(true);
        }}
        onViewPosition={(position) => setSelectedPosition(position)}
      />

      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        hideCloseAction={true}
        source="alerts_page"
      />

      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) {
            setTradeSymbol(null);
          }
        }}
        onGoToPosition={(position) => {
          if (position) {
            setSelectedPosition(position);
          }
        }}
      />

      <TickerSymbolDrawerDesktop
        open={isTickerDrawerOpen}
        onOpenChange={setIsTickerDrawerOpen}
        onSubmit={handleTickerSubmit}
        title="Analyze Token"
        submitButtonText="Analyze"
      />

      <TokenAnalysisDrawerDesktop
        userId={userId}
        symbol={analysisSymbol}
        open={isAnalysisDrawerOpen}
        onOpenChange={(open) => {
          setIsAnalysisDrawerOpen(open);
          if (!open) {
            setAnalysisSymbol(null);
          }
        }}
        onTrade={handleTradeFromAnalysis}
      />
    </>
  );
};
